import { Film, Loader2, Trash2, Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { confirmAvatarUpload, getAvatarUploadUrl } from '@/services/promptSetService';

const ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface AvatarUploadProps {
  promptSetId: string;
  promptIndex: number;
  hasVideo: boolean;
  videoDurationSec?: number;
  onUploaded: (videoUrl: string, durationSec: number) => void;
  onRemoved: () => void;
}

export function AvatarUpload({
  promptSetId,
  promptIndex,
  hasVideo,
  videoDurationSec,
  onUploaded,
  onRemoved,
}: AvatarUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const getVideoDuration = useCallback((file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration));
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(0);
      };
      video.src = URL.createObjectURL(file);
    });
  }, []);

  const uploadToS3 = useCallback((url: string, file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (fileInputRef.current) fileInputRef.current.value = '';

      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(t('promptSets.form.invalidFileType'));
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(t('promptSets.form.fileTooLarge'));
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
        const { uploadUrl, fileKey } = await getAvatarUploadUrl(promptSetId, promptIndex, file.type);
        await uploadToS3(uploadUrl, file);
        const durationSec = await getVideoDuration(file);
        await confirmAvatarUpload(promptSetId, promptIndex, fileKey, durationSec || 1);
        onUploaded(fileKey, durationSec || 1);
        toast.success(t('promptSets.form.videoUploaded'));
      } catch {
        toast.error(t('promptSets.form.videoUploadError'));
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [promptSetId, promptIndex, uploadToS3, getVideoDuration, onUploaded, t],
  );

  const handleRemove = useCallback(() => {
    onRemoved();
    toast.success(t('promptSets.form.videoRemoved'));
  }, [onRemoved, t]);

  return (
    <div className='mt-2'>
      <input
        ref={fileInputRef}
        type='file'
        accept='video/mp4,video/webm,video/quicktime'
        className='hidden'
        onChange={(e) => void handleFileSelect(e)}
      />

      {isUploading ? (
        <div className='flex items-center gap-2 text-sm text-gray-500'>
          <Loader2 size={14} className='animate-spin' />
          <span>{t('promptSets.form.uploadingVideo')}</span>
          <div className='h-1.5 flex-1 rounded-full bg-gray-200'>
            <div className='h-1.5 rounded-full bg-blue-600 transition-all' style={{ width: `${uploadProgress}%` }} />
          </div>
          <span className='text-xs'>{uploadProgress}%</span>
        </div>
      ) : hasVideo ? (
        <div className='flex items-center gap-2'>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700',
            )}>
            <Film size={12} />
            {t('promptSets.form.videoUploaded')}
            {videoDurationSec ? ` (${t('promptSets.form.videoDuration', { duration: videoDurationSec })})` : null}
          </span>
          <button
            type='button'
            onClick={handleRemove}
            className='inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50 hover:text-red-700'>
            <Trash2 size={12} />
            {t('promptSets.form.removeVideo')}
          </button>
        </div>
      ) : (
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          className='inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600'>
          <Upload size={12} />
          {t('promptSets.form.uploadVideo')}
        </button>
      )}
    </div>
  );
}
